/**
 * ═══════════════════════════════════════════════════════════════════════════════
 *  DEXEL WORKER BRIDGE - ASYNC MATERIAL REMOVAL API
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Web Worker üzerinden dexel hesaplamalarını yönetir.
 * Main thread blocking olmadan ağır işlemleri background'da yapar.
 *
 * Usage:
 *   const bridge = new DexelWorkerBridge();
 *   await bridge.init({ resX: 200, resY: 200, ... });
 *   await bridge.processBatch(samples, tool);
 *   const heightMap = await bridge.getHeightMap();
 */

import { DexelToolGeometry, DexelBBox, ToolSample } from './TripleDexelEngine';

export interface DexelWorkerConfig {
  resX: number;
  resY: number;
  resZ?: number;
  cellSize: number;
  bbox: DexelBBox;
  silindirYonModu: number;
}

export interface ProcessResult {
  hits: number;
  dirtyCount: number;
  dirtyHeights: {
    indices: number[];
    heights: number[];
  };
}

export class DexelWorkerBridge {
  private worker: Worker | null = null;
  private messageId = 0;
  private pendingMessages = new Map<number, {
    resolve: (value: any) => void;
    reject: (error: Error) => void;
  }>();
  private initialized = false;
  private config: DexelWorkerConfig | null = null;

  // ─── Initialization ───────────────────────────────────────────────────────

  async init(config: DexelWorkerConfig): Promise<boolean> {
    try {
      // Create worker
      this.worker = new Worker(
        new URL('./dexel.worker.ts', import.meta.url),
        { type: 'module' }
      );

      // Set up message handler
      this.worker.onmessage = this.handleMessage.bind(this);
      this.worker.onerror = this.handleError.bind(this);

      // Send init message
      const result = await this.sendMessage('init', config);

      if (result.success) {
        this.initialized = true;
        this.config = config;
        // DexelWorker initialized
        return true;
      }

      return false;
    } catch (err) {
      console.error('❌ DexelWorker init failed:', err);
      return false;
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Process a batch of tool positions
   */
  async processBatch(samples: ToolSample[], tool: DexelToolGeometry): Promise<ProcessResult> {
    if (!this.initialized || !this.worker) {
      throw new Error('Worker not initialized');
    }

    return this.sendMessage('process', { samples, tool });
  }

  /**
   * Process a swept move (prevPos → currPos) with tool interpolation
   * Worker does the same stepped interpolation as the CPU engine
   */
  async processSweptMove(
    prevX: number, prevY: number, prevZ: number,
    currX: number, currY: number, currZ: number,
    axisX: number, axisY: number, axisZ: number,
    tool: DexelToolGeometry
  ): Promise<ProcessResult> {
    if (!this.initialized || !this.worker) {
      throw new Error('Worker not initialized');
    }

    return this.sendMessage('processSweptMove', {
      prevX, prevY, prevZ,
      currX, currY, currZ,
      axisX, axisY, axisZ,
      tool
    });
  }

  /**
   * Get full height map
   */
  async getHeightMap(): Promise<Float32Array> {
    if (!this.initialized || !this.worker) {
      throw new Error('Worker not initialized');
    }

    const result = await this.sendMessage('getHeightMap', {});
    return result.heightMap;
  }

  /**
   * Get dirty cell info
   */
  async getDirtyInfo(): Promise<{ dirtyCount: number; dirtyBounds: any }> {
    if (!this.initialized || !this.worker) {
      throw new Error('Worker not initialized');
    }

    return this.sendMessage('getDirty', {});
  }

  /**
   * Clear dirty flags
   */
  async clearDirty(): Promise<void> {
    if (!this.initialized || !this.worker) {
      return;
    }

    await this.sendMessage('clearDirty', {});
  }

  async computeSmoothAndAO(
    smoothIterations: number,
    aoRadius: number,
    dirtyBounds?: { minX: number; maxX: number; minY: number; maxY: number }
  ): Promise<{ heightMap: Float32Array; aoMap: Float32Array }> {
    if (!this.initialized || !this.worker) {
      throw new Error('Worker not initialized');
    }
    return this.sendMessage('smoothAndAO', { smoothIterations, aoRadius, dirtyBounds });
  }

  /**
   * Dispose worker
   */
  dispose(): void {
    if (this.worker) {
      this.sendMessage('dispose', {}).catch(() => {});
      this.worker.terminate();
      this.worker = null;
    }
    this.initialized = false;
    this.pendingMessages.clear();
    // DexelWorker disposed
  }

  // ─── Config Getters ───────────────────────────────────────────────────────

  getResolution(): { resX: number; resY: number } {
    if (!this.config) return { resX: 0, resY: 0 };
    return { resX: this.config.resX, resY: this.config.resY };
  }

  getBBox(): DexelBBox {
    if (!this.config) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0, minZ: 0, maxZ: 0 };
    }
    return this.config.bbox;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  // ─── Internal Message Handling ────────────────────────────────────────────

  private sendMessage(type: string, payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('Worker not available'));
        return;
      }

      const id = this.messageId++;
      this.pendingMessages.set(id, { resolve, reject });

      this.worker.postMessage({ type, id, payload });

      // Timeout after 30 seconds
      setTimeout(() => {
        if (this.pendingMessages.has(id)) {
          this.pendingMessages.delete(id);
          reject(new Error(`Worker message timeout: ${type}`));
        }
      }, 30000);
    });
  }

  private handleMessage(e: MessageEvent): void {
    const { type, id, payload } = e.data;

    const pending = this.pendingMessages.get(id);
    if (!pending) return;

    this.pendingMessages.delete(id);

    if (type === 'error') {
      pending.reject(new Error(payload.message));
    } else {
      pending.resolve(payload);
    }
  }

  private handleError(e: ErrorEvent): void {
    console.error('DexelWorker error:', e.message);
    // Reject all pending messages
    this.pendingMessages.forEach(({ reject }) => {
      reject(new Error(`Worker error: ${e.message}`));
    });
    this.pendingMessages.clear();
  }
}

/**
 * Factory function - Worker'ı destekleyen ortamda kullan, yoksa null dön
 */
export function createDexelWorkerBridge(): DexelWorkerBridge | null {
  if (typeof Worker === 'undefined') {
    console.warn('⚠️ Web Workers not supported - falling back to main thread');
    return null;
  }

  return new DexelWorkerBridge();
}
